import { DrawerBox, Input } from '@/components'
import { useDrawer } from '@/hooks'
import { DrawerType } from '@/store/app'
import {
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { type EditBranchProps } from './type'
import {
  useCreateBranchesMutation,
  useLazyGetBranchesQuery,
  useUpdateBranchesMutation,
} from '@/api'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import { TrashIcon } from '@/modules/EditDictionarieVehicle/icons'
import { type IBranch } from '../../types'

export const EditBranch = (props: EditBranchProps) => {
  const { selectedBranch } = props
  const [branches, setBranches] = useState<IBranch[]>()
  const { organizationId } = useParams()
  const { hideDrawer, drawerOpened } = useDrawer()
  const [getBranches] = useLazyGetBranchesQuery()
  const [createBranches] = useCreateBranchesMutation()
  const [updateBranches] = useUpdateBranchesMutation()
  const branchesForm = useForm()
  const watchBranches = useWatch({
    control: branchesForm.control,
    name: 'branches',
  })
  const watchParentName = useWatch({
    control: branchesForm.control,
    name: 'parentName',
  })

  useEffect(() => {
    if (!selectedBranch?.parent?.id) {
      branchesForm.setValue('branches', [])
      branchesForm.setValue('parentName', undefined)
      return
    }
    if (!selectedBranch?.childs) {
      getBranches({
        companyId: Number(organizationId),
        params: {
          parentIds: Number(selectedBranch?.parent?.id),
        },
      }).then((response) => {
        branchesForm.setValue('branches', response?.data)
        branchesForm.setValue('parentName', selectedBranch?.parent?.name)
        setBranches(response?.data)
      })
    } else {
      branchesForm.setValue('branches', selectedBranch?.childs ?? [])
      branchesForm.setValue('parentName', selectedBranch?.parent?.name)
      // branchesForm.reset({
      //   branches: selectedBranch?.childs ?? [],
      //   parentName: selectedBranch?.parent?.name ?? '',
      // })
      setBranches(selectedBranch?.childs ?? [])
    }
  }, [selectedBranch])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (value: any) => {
    if (!selectedBranch) return

    if (!selectedBranch?.parent) {
      await createBranches({
        companyId: Number(organizationId),
        payload: {
          branches: [
            {
              name: value.parentName,
            },
          ],
        },
      })
      if (selectedBranch.refresh) selectedBranch?.refresh()
      hideDrawer()
      return
    }

    const valueBranches = value.branches.map((updated: IBranch) => ({
      ...updated,
      name: updated?.name?.trim(),
    }))

    const on_create =
      valueBranches
        ?.filter(
          (valueBranch: IBranch) =>
            !valueBranch?.id && Boolean(valueBranch?.name)
        )
        ?.map((branch: IBranch) => ({
          parentId: selectedBranch?.parent?.id,
          name: branch?.name,
        })) ?? []

    let on_update =
      valueBranches
        ?.filter(
          (valueBranch: IBranch) =>
            branches?.find(
              (branch: IBranch) =>
                branch?.id === valueBranch?.id &&
                branch?.name !== valueBranch?.name
            )
        )
        ?.map((branch: IBranch) => ({
          id: branch?.id,
          name: branch?.name,
          deleted: false,
        })) ?? []

    const on_delete =
      branches
        ?.filter(
          (branch: IBranch) =>
            !valueBranches?.find(
              (valueBranch: IBranch) => valueBranch?.id === branch?.id
            )
        )
        ?.map((branch: IBranch) => ({
          id: branch?.id,
          deleted: true,
        })) ?? []

    if (value.parentName !== selectedBranch.parent.name) {
      on_update = [
        ...on_update,
        {
          id: selectedBranch.parent.id,
          name: value.parentName,
          deleted: false,
        },
      ]
    }

    if (on_create?.length > 0) {
      await createBranches({
        companyId: Number(organizationId),
        payload: {
          branches: [...on_create],
        },
      })
    }
    if (on_update?.length > 0 || on_delete?.length > 0) {
      await updateBranches({
        companyId: Number(organizationId),
        payload: {
          branches: [...on_update, ...on_delete],
        },
      })
    }

    if (
      selectedBranch.refreshParent &&
      value.parentName !== selectedBranch.parent.name
    ) {
      selectedBranch?.refreshParent()
    }
    selectedBranch?.refresh()
    hideDrawer()
  }

  return (
    <DrawerBox
      onClose={hideDrawer}
      opened={DrawerType.EDIT_BRANCH === drawerOpened}
      bottom={
        <Stack gap={1.5}>
          <Button
            onClick={branchesForm.handleSubmit(onSubmit)}
            variant={'contained'}
            type={'submit'}
            disabled={!(watchParentName?.length > 0)}
          >
            Сохранить
          </Button>
          <Button variant={'contained'} color={'primary'} onClick={hideDrawer}>
            Отмена
          </Button>
        </Stack>
      }
      title={selectedBranch?.parent?.name ?? 'Новый филиал'}
    >
      <FormProvider {...branchesForm}>
        <Stack sx={{ gap: '16px' }}>
          <Input
            disabled={false}
            fullWidth
            sx={{ input: { padding: '8px 8px' } }}
            placeholder={'Название филиала'}
            name={'parentName'}
          />
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {watchBranches?.map((branch: any, index: number) => (
            <TableContainer
              key={index}
              sx={{
                border: '2px solid #D9D9D9',
                borderRadius: '4px',
              }}
            >
              <Table
                sx={{
                  '& .MuiTableCell-root': {
                    padding: '6px',
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Stack sx={{ flexDirection: 'row', gap: '6px' }}>
                        <Input
                          disabled={false}
                          fullWidth
                          sx={{ input: { padding: '8px 8px' } }}
                          placeholder={'Название подразделения'}
                          name={`branches.${index}.name`}
                        />
                        <IconButton
                          disabled={false}
                          onClick={() => {
                            const arr = [...watchBranches]
                            arr.splice(index, 1)
                            branchesForm.setValue('branches', arr)
                          }}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ))}
          {selectedBranch?.parent && (
            <Button
              // startIcon={<AddIcon />}
              variant="contained"
              disabled={false}
              onClick={() => {
                branchesForm.setValue('branches', [
                  ...(watchBranches ?? []),
                  { name: '' },
                ])
              }}
            >
              <AddIcon />
            </Button>
          )}
        </Stack>
      </FormProvider>
    </DrawerBox>
  )
}
